/**
 * The pieces a board is made of: a tile, and the item line inside it.
 *
 * Shared by the category boards (tiles are subcategories) and the master list
 * (tiles are priorities), so the two cannot drift into looking like different
 * products. Everything that differs between them -- what the header says, what
 * colour it is, whether an item shows its breadcrumb -- is a prop.
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { Item, ItemPriority } from '@/api/types'
import { PRIORITIES } from '@/api/types'
import { InlineStatus } from '@/components/InlineStatus'
import { categoryHeaderStyle } from '@/lib/color'
import { PRIORITY_LABEL, formatDue } from '@/lib/format'

/** Text colour for the inline priority control, which is now the only thing
 *  on a row stating its priority. */
const PRIORITY_TEXT: Record<ItemPriority, string> = {
  imminent: 'text-priority-imminent',
  high: 'text-priority-high',
  medium: 'text-priority-medium',
  low: 'text-priority-low',
}

export interface TileTint {
  background: string
  text: string
}

export function BoardTile({
  title,
  titleId,
  count,
  tint,
  onAddTask,
  addLabel,
  dragHandle,
  statusSlot,
  children,
  containerRef,
  style,
  isDragging,
}: {
  title: string
  titleId: string
  count: number
  tint: TileTint
  onAddTask?: () => void
  addLabel?: string
  dragHandle?: ReactNode
  /** The inline status editor, where the tile has one. */
  statusSlot?: ReactNode
  children: ReactNode
  containerRef?: (node: HTMLElement | null) => void
  style?: React.CSSProperties
  isDragging?: boolean
}) {
  return (
    <section
      ref={containerRef}
      style={style}
      className={`card overflow-hidden ${isDragging ? 'shadow-lg' : ''}`}
      aria-labelledby={titleId}
    >
      <div style={{ backgroundColor: tint.background, color: tint.text }} className="px-3 py-2.5">
        <div className="flex items-baseline justify-between gap-2">
          {dragHandle}
          <h3 id={titleId} className="min-w-0 flex-1 truncate text-base font-bold">
            {title}
          </h3>
          <span className="shrink-0 text-xs" style={{ opacity: 0.75 }}>
            {count} open
          </span>
        </div>

        {(statusSlot || onAddTask) && (
          <div className="mt-1 flex items-center justify-between gap-2">
            {statusSlot ?? <span />}
            {onAddTask && (
              <button
                type="button"
                onClick={onAddTask}
                style={{ color: tint.text, borderColor: `${tint.text}55` }}
                className="shrink-0 rounded-md border px-2 py-0.5 text-xs font-semibold transition-colors hover:bg-black/5"
                title={addLabel}
              >
                + Task
              </button>
            )}
          </div>
        )}
      </div>

      {children}
    </section>
  )
}

/**
 * One item inside a tile.
 *
 * `showBreadcrumb` is the main difference between the two boards: inside a
 * subcategory tile the heading already says where the item lives, while in a
 * priority tile the items come from everywhere and the breadcrumb is the only
 * thing that says where each one belongs.
 *
 * There is no priority dot. The dropdown on every row states the priority in
 * words and in colour, so a dot beside it said the same thing twice.
 */
export function BoardItem({
  item,
  today,
  onComplete,
  onItemStatus,
  onPriorityChange,
  showBreadcrumb = false,
}: {
  item: Item
  today: string
  onComplete: (item: Item) => void
  onItemStatus: (item: Item, next: string | null) => void | Promise<void>
  onPriorityChange: (item: Item, priority: ItemPriority) => void
  showBreadcrumb?: boolean
}) {
  const due = formatDue(item.due_date, today)

  // The same colour and computed text colour the category's own band uses, so
  // a row in a priority tile still says which category it came from at a
  // glance rather than only in words.
  const breadcrumb = categoryHeaderStyle(
    item.subcategory.category.color,
    item.subcategory.category.id,
  )

  return (
    <li className="group border-b border-line px-3 py-2 last:border-b-0 hover:bg-navy-softer">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => onComplete(item)}
          aria-label={`Mark ${item.title} done`}
          title="Mark done"
          className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-line-strong text-transparent transition-colors hover:border-success hover:text-success"
        >
          <span aria-hidden className="text-xs leading-none">
            &#10003;
          </span>
        </button>

        <div className="min-w-0 flex-1">
          {showBreadcrumb && (
            <div className="mb-0.5 flex">
              <span
                className="pill max-w-full"
                style={{ backgroundColor: breadcrumb.background, color: breadcrumb.text }}
                title={`${item.subcategory.category.name}: ${item.subcategory.name}`}
              >
                <span className="truncate">
                  {item.subcategory.category.name}
                  <span aria-hidden style={{ opacity: 0.7 }}>
                    :{' '}
                  </span>
                  {item.subcategory.name}
                </span>
              </span>
            </div>
          )}

          {/* Two lines rather than one: in a narrow tile a single truncated
              line cuts most titles at the point they start being informative. */}
          <Link
            to={`/items/${item.id}`}
            className="line-clamp-2 block text-sm font-medium text-ink hover:text-navy hover:underline"
            title={item.title}
          >
            {item.title}
          </Link>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <InlineStatus
              value={item.status_note}
              onSave={(next) => onItemStatus(item, next)}
              size="xs"
              label={`Status for ${item.title}`}
            />
            {due && (
              <span
                className={`text-xs ${
                  due.overdue
                    ? 'font-semibold text-crimson'
                    : due.soon
                      ? 'font-semibold text-warning-text'
                      : 'text-ink-3'
                }`}
              >
                {due.text}
              </span>
            )}
            {item.checklist_total > 0 && (
              <span className="text-xs text-ink-3">
                {item.checklist_done}/{item.checklist_total}
              </span>
            )}
            {item.manual_rank !== null && (
              <span className="pill bg-navy-soft text-navy" title="Pinned to a manual position">
                &#9679; pinned
              </span>
            )}

            {/* A bare select rather than a bordered field: on a dense tile a
                full input control per row reads as a form. It still opens the
                platform picker on a phone and is keyboard reachable. */}
            <label className="sr-only" htmlFor={`tile-priority-${item.id}`}>
              Priority for {item.title}
            </label>
            <select
              id={`tile-priority-${item.id}`}
              value={item.priority}
              onChange={(event) =>
                onPriorityChange(item, event.target.value as ItemPriority)
              }
              className={`ml-auto shrink-0 cursor-pointer rounded-sm border-0 bg-transparent py-0 pl-1 pr-4 text-xs font-semibold focus:ring-0 ${
                PRIORITY_TEXT[item.priority]
              }`}
              title="Change priority"
            >
              {PRIORITIES.map((priority) => (
                <option key={priority} value={priority}>
                  {PRIORITY_LABEL[priority]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </li>
  )
}

export function TileEmpty({ children }: { children: ReactNode }) {
  return <p className="px-3 py-6 text-center text-sm text-ink-3">{children}</p>
}

export function TileOverdue({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <p className="border-b border-line bg-crimson-soft px-3 py-1 text-xs font-semibold text-crimson">
      {count} overdue
    </p>
  )
}
